/**
 * ActivationsMenuValidation / ValidationTable - dynamic validation table (ITRTL-720)
 *
 * Differs from MenuValidation/ValidationTable in two ways:
 *  - "By Validation Type" filter chips are built from `applicableValidations`
 *    (the aggregator's actual rule set) instead of the static VALIDATION_FIELD_TYPES enum.
 *  - Table columns are built from the same dynamic list.
 *
 * Filter semantics are identical: item-type filter (AND across selected types),
 * validation-type filter (OR — match if any selected field is MISSING/INVALID).
 */

/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import ValidationRow from "./ValidationRow";
import {
  VALIDATION_TYPES,
  VALIDATION_TYPE_NAMES,
  VALIDATION_STATUS
} from "../../utils/enums";
import { getValidationLabel, orderValidationTypes } from "../../utils/validationFields";

const ValidationTable = ({
  validationResults,
  applicableValidations,
  metadata,
  onOpenModal,
  onOpenPanel
}) => {
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);
  const [selectedValidationFields, setSelectedValidationFields] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  // Ordered, dynamic list of validation types for this aggregator
  const validationTypes = orderValidationTypes(applicableValidations);

  if (!validationResults || validationResults.length === 0) {
    return (
      <div className="ls-validation-table-empty">
        <p>No validation results available.</p>
      </div>
    );
  }

  // Only show item-type chips that actually appear in the result set
  const presentItemTypes = Object.values(VALIDATION_TYPES).filter((t) =>
    validationResults.some((item) => item.type === t)
  );

  // Multi-criteria filter logic (same semantics as MenuValidation)
  const filteredResults = validationResults.filter((item) => {
    const matchesItemType =
      selectedItemTypes.length === 0 || selectedItemTypes.includes(item.type);

    const matchesValidationField =
      selectedValidationFields.length === 0 ||
      selectedValidationFields.some((fieldKey) => {
        const validation = item.validation && item.validation[fieldKey];
        return (
          validation &&
          (validation.status === VALIDATION_STATUS.MISSING ||
            validation.status === VALIDATION_STATUS.INVALID)
        );
      });

    return matchesItemType && matchesValidationField;
  });

  const handleItemTypeChange = (type) => {
    setSelectedItemTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleValidationFieldChange = (fieldKey) => {
    setSelectedValidationFields((prev) =>
      prev.includes(fieldKey) ? prev.filter((f) => f !== fieldKey) : [...prev, fieldKey]
    );
  };

  const getFilterLabel = () => {
    const totalActive = selectedItemTypes.length + selectedValidationFields.length;
    if (totalActive === 0) return "Filter";
    return `Filter (${totalActive})`;
  };

  return (
    <div className="ls-validation-table-wrapper">
      <div className="ls-validation-table-header">
        <h2 className="ls-validation-table-title">Items with Missing Fields</h2>
        <div className="ls-validation-filter" ref={filterRef}>
          <button
            className="ls-validation-filter-btn"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            <svg className="ls-validation-filter-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.16675 5.83333H15.8334M6.66675 10H13.3334M8.75008 14.1667H11.2501" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{getFilterLabel()}</span>
          </button>
          {isFilterOpen && (
            <div className="ls-validation-filter-dropdown">
              {/* Category 1: By Item Type (dynamic — only types present in results) */}
              <div className="ls-validation-filter-category">
                <div className="ls-validation-filter-category-header">By Item Type</div>
                {presentItemTypes.map((type) => (
                  <label key={type} className="ls-validation-filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedItemTypes.includes(type)}
                      onChange={() => handleItemTypeChange(type)}
                    />
                    <span>{VALIDATION_TYPE_NAMES[type] || type}</span>
                  </label>
                ))}
              </div>

              {/* Divider */}
              <div className="ls-validation-filter-divider" />

              {/* Category 2: By Validation Type (dynamic — from applicableValidations) */}
              <div className="ls-validation-filter-category">
                <div className="ls-validation-filter-category-header">By Validation Type</div>
                {validationTypes.map((type) => (
                  <label key={type} className="ls-validation-filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedValidationFields.includes(type)}
                      onChange={() => handleValidationFieldChange(type)}
                    />
                    <span>{getValidationLabel(type)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table container with independent vertical scrolling */}
      <div className="ls-validation-table-container">
        <table className="ls-validation-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              <th>Category</th>
              {validationTypes.map((type) => (
                <th key={type}>{getValidationLabel(type)}</th>
              ))}
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((result) => (
              <ValidationRow
                key={result.id || `${result.type}-${result.name}`}
                validationResult={result}
                validationTypes={validationTypes}
                metadata={metadata}
                onOpenModal={onOpenModal}
                onOpenPanel={onOpenPanel}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ValidationTable;
